// Building Better Shooters. A live shooting clinic from the San Diego coaches
// retreat. Mux: JrW00o6A5Dr2YsXj7D02Z25GYGYH9vuFUjejBbwKHYgTM (0:37:13)
//
// The cards are BAM's editorial summary of the clinic, not a transcript.
// Only text inside quotation marks is Coleman's own words, and every
// quote carries the time he says it. talkQuotes.test.js checks each one
// against knowledge-base/transcripts/masterclass/building-better-shooters.md
// (the clinic's captions), so a quote that isn't in the talk can't ship.
// Summary copy keeps his hedges and stays in the third person so it
// can't read as his voice. No em dashes.

export const SHOOTING_CLINIC_MODULE_KEY = "building-better-shooters";

const VIDEO = "JrW00o6A5Dr2YsXj7D02Z25GYGYH9vuFUjejBbwKHYgTM";

export const SHOOTING_CLINIC_MODULE = {
  key: SHOOTING_CLINIC_MODULE_KEY,
  title: "Building Better Shooters",
  sub: "A live shooting clinic. The four types of shooting work, how to balance them, and how to coach young shooters.",
  coverVideoId: VIDEO,
  duration: "37min clinic plus 9 lessons",
  chapters: [
    {
      id: "watch",
      title: "Watch the clinic",
      blocks: [
        { kind: "text", md:
          "Filmed live at the retreat. It breaks building a shooter into four types of work, then gets into how to actually balance them, including with younger players. Watch it through. The cards below recap it in order." },
        { kind: "text", md:
          "**About these cards.** They're BAM's summary of the clinic, written from the recording. They're not a transcript. Only the lines in quotation marks are Coleman's own words, each with the time he says it in the video." },
        { kind: "video", muxPlaybackId: VIDEO,
          caption: "Building Better Shooters. Full session." },
      ],
    },
    {
      id: "four-types",
      title: "1. The four types of shooting work.",
      blocks: [
        { kind: "text", md:
          "**Almost everything you do with a shooter falls into four buckets.**" },
        { kind: "text", md:
          "- **Exploration shooting.** Purposely change the technique every rep.\n- **Anchor shooting.** Find a stable shot through chaos.\n- **Stress shooting.** Add external stress on top of the shot.\n- **Groove shooting.** Rep the same pattern and feel good." },
        { kind: "text", md:
          "None of these is better than another. The art is the mix, and the mix is fully contextual to the player in front of you." },
      ],
    },
    {
      id: "exploration",
      title: "2. Exploration shooting.",
      blocks: [
        { kind: "text", md:
          "**Purposely vary your technique on every rep so the brain explores the full range of solutions.**" },
        { kind: "text", md:
          "The shooter's attention is on trying new things. Different footwork and base, different release height, different arc, jumping a little one way or the other. You want the whole bandwidth of the technique, and you want to get comfortable shooting from non-perfect positions." },
        { kind: "text", md:
          "Think of setting up the fingerprint scan on your phone. You move your thumb around so it captures every groove and builds a full picture. You are doing that with the shot." },
        { kind: "text", md:
          "This is differential learning in its truest sense. Exploring different solutions often helps the brain self-organize toward the better ones. With a young player whose shot is all over the place, it can start with trying everything. But with younger players, exploration \"can be good, but we want to limit it in a very intentional way.\" (5:51)" },
        { kind: "text", md:
          "Keep it less about makes and misses. The moment you say make six of ten, it turns into anchor shooting." },
      ],
    },
    {
      id: "anchor",
      title: "3. Anchor shooting.",
      blocks: [
        { kind: "text", md:
          "**Find a stable, repeatable shot through the chaos. This is the opposite of exploration.**" },
        { kind: "text", md:
          "Coleman says this distinction is recent for him: \"I never really made this distinction until probably the last few months where I would do both with kind of the same intention, but now I've realized they're completely different.\" (4:24)" },
        { kind: "text", md:
          "You enter the shot through internal chaos. A full spin, moving backwards, looking over the shoulder, a bad pass on purpose. The technique will not be identical because you entered it differently, but the goal is stability, not variety. You are manipulating how the player enters the shot to challenge how stable they can stay." },
        { kind: "text", md:
          "\"Learn how to make the imperfect perfect, but also learn how to deal with imperfection.\" (15:50) If you can't make it perfect you can still make it work, while you keep hunting the perfect." },
        { kind: "text", md:
          "Isolate the quality like an allergy test. If you challenge vision hard and the shot still falls, the problem is somewhere else, maybe weight distribution while moving backwards. Test one thing at a time. And stay honest. Adding a reload dribble can quietly switch which quality you are actually training." },
      ],
    },
    {
      id: "stress",
      title: "4. Stress shooting.",
      blocks: [
        { kind: "text", md:
          "**Add external stress on top of the shot. This is where contested and game-based shooting live.**" },
        { kind: "text", md:
          "Stressors to layer in: a contest or defender, which is probably the best one, plus noise, lights, a push in the air, tactical rules, pressure, and fatigue. 2v1 shooting is a staple." },
        { kind: "text", md:
          "Under stress the player has to decide in the moment whether to anchor the shot or explore a solution. That discernment is the point, and it is where exploration and anchor work get cashed in." },
        { kind: "text", md:
          "A simple pressure game, with space between shots and a footwork change every shot, is probably one of the best things you can do. Whatever you call that game, \"we overthink it.\" (23:29)" },
      ],
    },
    {
      id: "fatigue",
      title: "5. Fatigue, and what actually breaks down.",
      blocks: [
        { kind: "text", md:
          "**When a shooter gets tired, it is often not the legs that break down. It is the sequencing.**" },
        { kind: "text", md:
          "Coleman points to a study here, which he said he'd send the group: \"many times it's not the legs that's breaking down. It's the sequencing that's breaking down.\" (21:00) We tell players to use their legs. They end up jumping more, shooting higher off the jump, and flicking it, and the sequence falls apart." },
        { kind: "text", md:
          "Cue for feel and fluidity instead. Attack the ground. Be fluid. Everything down, everything up. You get there through energy transfer, not through trying harder with the legs. Teach players what to say to themselves when they are tired. He says there's research behind feel-based cues like these as well." },
        { kind: "text", md:
          "A coach in the room added the one he'd forgotten: shoot it higher. You cannot launch the ball high with no fluidity, so a higher target tends to pull the body back into sequence. \"Form often follows function.\" (22:15)" },
      ],
    },
    {
      id: "groove",
      title: "6. Groove shooting.",
      blocks: [
        { kind: "text", md:
          "**Stand there, groove the same pattern, and knock it down. Repetition is not the enemy.**" },
        { kind: "text", md:
          "\"Nobody's ever argued that repetition is not important. ... a fundamental truth of how we learn as humans.\" (24:30) Groove shooting builds confidence, rhythm, and the flow state, and anyone who plays knows how much it matters to just see the ball go through." },
        { kind: "text", md:
          "You can hybridize it. Keep grooving the pattern but add a light tap, a small contest, or a little fatigue, so it stays useful without losing the feel-good purpose." },
      ],
    },
    {
      id: "difficulty-by-percentage",
      title: "7. Set the difficulty by the percentage.",
      blocks: [
        { kind: "text", md:
          "**Your practice percentage should sit around the in-game percentage, if not a little lower.**" },
        { kind: "text", md:
          "If a player is shooting 70 percent in a workout, they're probably not in the optimal zone of improvement. For threes, design tasks and shots that get them below 40 percent. Coleman calls it \"not a perfect rule of thumb.\" (6:34)" },
        { kind: "text", md:
          "The scoring system is itself a constraint, so use it on purpose. A make-six-of-ten rule will turn exploration into anchor, because the moment makes matter, players stop exploring and start protecting the number." },
      ],
    },
    {
      id: "young-shooters",
      title: "8. Coaching young shooters.",
      blocks: [
        { kind: "text", md:
          "**Lean on groove and feel, keep it tied to the game, and do not lock a growing kid into a technique.**" },
        { kind: "text", md:
          "For a young player whose mechanics are all over the place, most of the work is groove: \"probably 70 80 percent more groove stuff maybe even less.\" (29:19) But don't live there. Coleman cites research and a pretty strong case that going too far into groove while learning makes the shot robotic and tougher to adjust later, so you still sprinkle in exploration and anchor." },
        { kind: "text", md:
          "Keep the shooting connected to the game. A kid shooting in a 3v3 catches in rhythm and gets it up. A kid shooting alone shoots to not miss and overthinks it. Coleman says many high-level players shoot better contested than uncontested for the same reason, without putting a percentage on it." },
        { kind: "text", md:
          "Cue for feel, not mechanics. Teach a young player to hit the ground for a smooth shot before you teach a perfect elbow angle. Once a player is locked into a super rigid shot, it's very tough to get them out of it. And a ten year old remembers shoot over a giraffe far better than get your elbow above your shoulder." },
        { kind: "text", md:
          "Remember the growing body changes the technique. The release that works at ten will not be the release once they are stronger and the ball is smaller. Build adaptability, not a frozen form. The best technique tool is having them watch good shooters and imitate them, so bring young players around real shooters and let them rebound and watch. \"The best way for humans to learn is by seeing and doing, not by hearing you as a coach tell them what to do.\" (34:41)" },
        { kind: "text", md:
          "On the balance itself, he's candid: \"It's one that I don't think anyone has the right answer for.\" (32:13)" },
      ],
    },
    {
      id: "no-formula",
      title: "9. There is no formula.",
      blocks: [
        { kind: "text", md:
          "\"I wish I could give you an answer and be like 20% should be this, 30% should be this.\" (36:31)" },
        { kind: "text", md:
          "If someone handed you the exact percentages, you would turn into a robot and run the same thing on every athlete, and you would stop enjoying it, because that is not coaching." },
        { kind: "text", md:
          "Coaching is figuring out what this specific player needs, the four year old, the high schooler, the NBA guy, and finding the solution for them. You cannot look it up online and apply it. You think, you try things, you go through trial and error, and you find what works for the athlete in front of you." },
      ],
    },
  ],
};
